/**
 * @fileoverview Utility for per-interaction-site invocation descriptions.
 * Uses an exhaustive switch statement so that adding a new interaction site
 * in shared_constants.json produces a compile-time error here.
 * @owner @Appboy/intelligence
 */

import type { I18nT } from "@lib/dashboardI18n";
import { exhaustiveCheck } from "@lib/exhaustiveCheck";

import type { InteractionSite } from "./types";
import { INTERACTION_SITES } from "./types";

/**
 * Returns a translated sentence describing what one invocation means for the
 * given interaction site, or null for sites whose invocation-limit UI is not
 * shown (QA agents) and for an undefined site (e.g. agent not yet loaded).
 *
 * Uses an exhaustive switch so that adding a new interaction site constant
 * to shared_constants.json causes a compile-time error here, prompting the
 * developer to add the new case.
 */
export const interactionSiteInvocationDescriptionGetter = (
  t: I18nT<"agent-details">,
  site: InteractionSite | undefined
): string | null => {
  if (site === undefined) {
    return null;
  }
  switch (site) {
    case INTERACTION_SITES.CANVAS:
      return t("invocation-description-canvas", "Each invocation is one user processed by your Canvas agent.");
    case INTERACTION_SITES.CATALOG:
      return t("invocation-description-catalog", "Each invocation is one row processed by your Catalog agent.");
    case INTERACTION_SITES.CAMPAIGN_SETUP:
    case INTERACTION_SITES.CANVAS_QA:
      return null;
    default:
      exhaustiveCheck(site);
      return null;
  }
};
